// src/features/privateAlbum/hooks/usePrivateAlbum.js

import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAlbums,
  fetchAlbumById,
  createAlbum,
  updateAlbum,
  deleteAlbum,
  archiveAlbum,
  restoreAlbum,
  fetchAlbumPhotos,
  uploadPhotos,
  deletePhoto,
  updatePhoto,
  setCoverPhoto,
  shareAlbum,
  revokeAccess,
  updateAccessRole,
  fetchAlbumStats,
  fetchOverallStats,
  setFilters,
  clearFilters,
  clearError,
  clearSuccess,
} from "../../../app/slice/privateAlbumSlice";

export const usePrivateAlbum = () => {
  const dispatch = useDispatch();
  const {
    albums,
    selectedAlbum,
    photos,
    accessList,
    stats,
    loading,
    error,
    success,
    filters,
    sortBy,
    sortOrder,
  } = useSelector((state) => state.privateAlbum);

  // Album CRUD
  const getAlbums = useCallback(
    (filters = {}) => {
      dispatch(fetchAlbums(filters));
    },
    [dispatch],
  );

  const getAlbumById = useCallback(
    (id) => {
      dispatch(fetchAlbumById(id));
    },
    [dispatch],
  );

  const createNewAlbum = useCallback(
    (data) => {
      return dispatch(createAlbum(data)).unwrap();
    },
    [dispatch],
  );

  const updateExistingAlbum = useCallback(
    (id, data) => {
      return dispatch(updateAlbum({ id, data })).unwrap();
    },
    [dispatch],
  );

  const deleteExistingAlbum = useCallback(
    (id) => {
      return dispatch(deleteAlbum(id)).unwrap();
    },
    [dispatch],
  );

  const archiveExistingAlbum = useCallback(
    (id) => {
      return dispatch(archiveAlbum(id)).unwrap();
    },
    [dispatch],
  );

  const restoreExistingAlbum = useCallback(
    (id) => {
      return dispatch(restoreAlbum(id)).unwrap();
    },
    [dispatch],
  );

  // Photo Management
  const getPhotos = useCallback(
    (albumId) => {
      dispatch(fetchAlbumPhotos(albumId));
    },
    [dispatch],
  );

  const uploadNewPhotos = useCallback(
    (albumId, photosData, uploadedBy) => {
      return dispatch(
        uploadPhotos({ albumId, photosData, uploadedBy }),
      ).unwrap();
    },
    [dispatch],
  );

  const deleteExistingPhoto = useCallback(
    (photoId) => {
      return dispatch(deletePhoto(photoId)).unwrap();
    },
    [dispatch],
  );

  const updateExistingPhoto = useCallback(
    (photoId, data) => {
      return dispatch(updatePhoto({ photoId, data })).unwrap();
    },
    [dispatch],
  );

  const setCover = useCallback(
    (albumId, photoId) => {
      return dispatch(setCoverPhoto({ albumId, photoId })).unwrap();
    },
    [dispatch],
  );

  // Access Management
  const shareWithClient = useCallback(
    (albumId, email, name, role, invitedBy) => {
      return dispatch(
        shareAlbum({ albumId, email, name, role, invitedBy }),
      ).unwrap();
    },
    [dispatch],
  );

  const revokeAccessFromClient = useCallback(
    (accessId) => {
      return dispatch(revokeAccess(accessId)).unwrap();
    },
    [dispatch],
  );

  const changeAccessRole = useCallback(
    (accessId, role) => {
      return dispatch(updateAccessRole({ accessId, role })).unwrap();
    },
    [dispatch],
  );

  // Statistics
  const getAlbumStats = useCallback(
    (albumId) => {
      dispatch(fetchAlbumStats(albumId));
    },
    [dispatch],
  );

  const getOverallStats = useCallback(() => {
    dispatch(fetchOverallStats());
  }, [dispatch]);

  // Filters
  const updateFilters = useCallback(
    (newFilters) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  // Clear states
  const clearErrorState = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearSuccessState = useCallback(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  return {
    // State
    albums,
    selectedAlbum,
    photos,
    accessList,
    stats,
    loading,
    error,
    success,
    filters,
    sortBy,
    sortOrder,

    // Album CRUD
    getAlbums,
    getAlbumById,
    createNewAlbum,
    updateExistingAlbum,
    deleteExistingAlbum,
    archiveExistingAlbum,
    restoreExistingAlbum,

    // Photo Management
    getPhotos,
    uploadNewPhotos,
    deleteExistingPhoto,
    updateExistingPhoto,
    setCover,

    // Access Management
    shareWithClient,
    revokeAccessFromClient,
    changeAccessRole,

    // Statistics
    getAlbumStats,
    getOverallStats,

    // Filters
    updateFilters,
    resetFilters,

    // Clear states
    clearErrorState,
    clearSuccessState,
  };
};

export default usePrivateAlbum;
